import React from 'react';
import { cn } from '@/lib/utils';
import { FramedAvatar } from './FramedAvatar';

interface StudentDisplayProps {
  student: {
    id: string;
    name?: string;
    full_name?: string;
    username?: string;
    profile_picture_url?: string;
    belt_rank?: string;
  };
  className?: string;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export function StudentDisplay({ 
  student, 
  className = "",
  showRemoveButton = false,
  onRemove,
  size = 'sm'
}: StudentDisplayProps) {
  const studentName = student.name || student.full_name || 'Unknown Student';
  
  const initials = studentName
    ? studentName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'UN';

  // Size configurations
  const sizeConfig = {
    sm: { avatarSize: 24, frameSize: { w: 32, h: 36 }, fontSize: 10 },
    md: { avatarSize: 32, frameSize: { w: 40, h: 44 }, fontSize: 12 },
    lg: { avatarSize: 42, frameSize: { w: 60, h: 66 }, fontSize: 16 }
  };

  const config = sizeConfig[size];

  // Determine if student is manual (no username or username is 'manual')
  const isManual = !student.username || student.username === 'manual';

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <FramedAvatar
        src={student.profile_picture_url}
        alt={studentName}
        initials={initials}
        beltRank={student.belt_rank}
        size={config.avatarSize}
        frameSize={config.frameSize}
        fontSize={config.fontSize}
        username={student.username && student.username !== 'manual' ? student.username : undefined}
        isManual={isManual}
      />
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 truncate capitalize">
          {studentName}
        </span>
        {student.username && student.username !== 'manual' && (
          <span className="text-xs text-gray-500 truncate">
            @{student.username}
          </span>
        )}
      </div>
      {showRemoveButton && onRemove && (
        <button
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 p-1"
          title="Remove student"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
